import { useEffect, useState } from 'react';
import { href, useParams } from 'react-router-dom';

import { getServiceCaseDetails } from '@/controller/serviceCase';
import { Spinner, toast } from '@/shared/components/UI';
import Breadcrumb from '@/shared/components/UI/Breadcrumb';
import SectionTitle from '@/shared/components/UI/SectionTitle';
import InfoGrid from '@/shared/components/details/InfoGrid';

const ViewServiceCaseDetails = () => {
    const { case_no } = useParams();
    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState(null);

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            try {
                const res = await getServiceCaseDetails(case_no);
                if (res.status === 200 && res.data) {
                    setDetails(res.data.data);
                } else if (ret.status !== 401) {
                    // Don't show toast for 401 errors - handled by global interceptor
                    toast.error(ret.data?.error?.message);
                }
            } catch (e) {
                toast.error('An error occurred while fetching service case details.');
            }
            setLoading(false);
        };

        fetchDetails();
    }, [case_no]);

    return (
        <div className="relative flex size-full min-h-screen flex-col bg-white">
            <div className="layout-container flex h-full grow flex-col">
                <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5">
                    <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
                        {/* Breadcrumb */}
                        <Breadcrumb
                            items={[{ label: 'Cases', href: '/member/view-service-case' }, { label: 'Case Details' }]}
                        />
                        {/* Header */}
                        <div className="flex flex-wrap justify-between gap-3 p-4">
                            <div className="flex min-w-72 flex-col gap-3">
                                <p className="text-[#0d141c] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight">
                                    Case Details
                                </p>
                                <div className="flex flex-row space-between items-center gap-x-3">
                                    {details?.case_no && (
                                        <p className="text-[#49739c] text-base font-mono font-semibold whitespace-nowrap">
                                            # {details.case_no}
                                        </p>
                                    )}
                                    {details?.status_data && (
                                        <button
                                            type="button"
                                            className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-8 px-4 text-white text-sm font-medium leading-normal"
                                            style={{ backgroundColor: details.status_data.color || '#d4d9e3' }}
                                            tabIndex={-1} // makes it non-focusable if it's just a tag
                                            disabled // makes it non-clickable if it's just a tag
                                        >
                                            <span className="truncate">{details.status_data.name}</span>
                                        </button>
                                    )}
                                </div>
                                {/* <p className="text-[#49739c] text-sm font-normal leading-normal">
                  View and manage the details of your service case.
                </p> */}
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex justify-center items-center min-h-[300px]">
                                <Spinner spinning />
                            </div>
                        ) : (
                            <>
                                {/* Case Information */}
                                <SectionTitle>Case Information</SectionTitle>
                                <InfoGrid
                                    items={[
                                        { label: 'Service Center', value: details?.service_center.name },
                                        { label: 'Service Type', value: details?.service_type.name },
                                        { label: 'Subject', value: details?.subject },
                                        { label: 'Physical Unit Condition', value: details?.unit_condition },
                                        { label: 'Description', value: details?.description },
                                        {
                                            label: 'Attachments',
                                            value:
                                                Array.isArray(details?.attachments) &&
                                                details.attachments.length > 0 ? (
                                                    <ul>
                                                        {details.attachments.map((file, i) => (
                                                            <li key={file.id || i}>
                                                                <a
                                                                    href={file.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-[#2563eb] underline hover:no-underline"
                                                                >
                                                                    {file.name || `File ${i + 1}`}
                                                                </a>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : null,
                                        },
                                    ]}
                                />

                                {/* Product Information */}
                                <SectionTitle>Product Information</SectionTitle>
                                <InfoGrid
                                    items={[
                                        { label: 'Name', value: details?.product?.name },
                                        { label: 'Serial Number', value: details?.serial_number },
                                    ]}
                                />

                                {/* Customer Information */}
                                <SectionTitle>Customer Information</SectionTitle>
                                <InfoGrid
                                    items={[
                                        { label: 'First Name', value: details?.customer?.first_name },
                                        { label: 'Last Name', value: details?.customer?.last_name },
                                        { label: 'Email Address', value: details?.customer?.email },
                                        {
                                            label: 'Phone Number',
                                            value:
                                                details?.customer?.phone.prefix && details?.customer?.phone.no
                                                    ? details?.customer?.phone.prefix + details?.customer?.phone.no
                                                    : undefined,
                                        },
                                    ]}
                                />

                                {/* Person/Contact Information */}
                                <SectionTitle>Person/Contact Information</SectionTitle>
                                <InfoGrid
                                    items={[
                                        { label: 'First Name', value: details?.contact?.first_name },
                                        { label: 'Last Name', value: details?.contact?.last_name },
                                        { label: 'Email Address', value: details?.contact?.email },
                                        {
                                            label: 'Phone Number',
                                            value:
                                                details?.contact?.phone_no_prefix && details?.contact?.phone_no
                                                    ? details?.contact?.phone_no_prefix + details?.contact?.phone_no
                                                    : undefined,
                                        },
                                    ]}
                                />

                                {/* Resolution Information */}
                                <SectionTitle>Resolution Information</SectionTitle>
                                <InfoGrid items={[{ label: 'Resolution', value: details?.resolution, colSpan: 2 }]} />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewServiceCaseDetails;
